/**
 * Delivers a finished workflow run's RESULT into the chat, and records whether
 * the user actually ended up seeing it.
 *
 * ## Why this is a separate concern from the chat stream
 *
 * Workflows are asynchronous. `workflow_run` returns in ~25ms and the run then
 * executes for minutes, so the turn that started it narrates, ends, and closes
 * its SSE stream long before the run finishes. The result is written to the
 * session transcript server-side and has to be PULLED onto the screen
 * afterwards — by this hook.
 *
 * That is why no amount of stream telemetry could see this failing: 30 days of
 * production `turn.stream` records showed 2,664 turns with zero disconnects and
 * zero missing terminal frames. The stream is healthy and already closed.
 *
 * ## The bug this exists to fix (reported 2026-09-05)
 *
 * The refresh is skipped while a turn is streaming, so a live turn is never
 * disrupted. That part is right. But it used to be DROPPED rather than
 * deferred, and the effect that schedules it is keyed on run-status
 * transitions — which never fire again once a run has reached terminal. So a
 * user who sent another message while waiting out a multi-minute run (the
 * obvious thing to do) lost the result permanently: no error, no retry, nothing
 * short of a manual page reload.
 *
 * Queue-and-flush fixes it, in the same shape `useAgentChat` already uses for
 * upload notes: hold the pending refresh, run it the moment the chat is idle.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { WorkflowRunRecord } from '../../agents/workflowsApi.ts';
import {
  deliverRunResult,
  recordWorkflowDelivery,
  type PendingRunReload,
  type WorkflowDeliveryEvent,
} from './workflowDelivery.ts';
import type { ReloadOutcome } from './useSessions.ts';

/**
 * How long to wait after a run reaches terminal before refreshing.
 *
 * The turn wraps up shortly after the tool returns (narration + the runtime's
 * own writes); refreshing instantly would race the transcript write and restore
 * a transcript that does not yet contain the result.
 */
export const RUN_RESULT_REFRESH_DELAY_MS = 6000;

export interface RunResultDeliveryInput {
  /** The watched run, from `useWorkflowRunProgress`. */
  readonly record: WorkflowRunRecord | undefined;
  /** The chat's status; `'streaming'` means a turn (or stall recovery) is live. */
  readonly chatStatus: string;
  /** The session the run belongs to. A switch invalidates a pending refresh. */
  readonly sessionId: string;
  /** Re-pull the current transcript. Resolves with what happened. */
  readonly reload: () => Promise<ReloadOutcome>;
  /**
   * Run once when a run reaches terminal, regardless of chat status — the Files
   * panel refresh. A run's document writes are server-side, so no `doc_*` tool
   * call ever crossed the chat stream to trigger the usual refresh.
   */
  readonly onRunSettled: () => void;
}

export function useRunResultDelivery(input: RunResultDeliveryInput): void {
  const { record, chatStatus, sessionId, reload, onRunSettled } = input;
  const runStatus = record?.status;

  // Read at fire time so a turn the user started meanwhile is never disrupted,
  // and so the 6s timer does not need re-creating on every keystroke.
  const chatStatusRef = useRef(chatStatus);
  chatStatusRef.current = chatStatus;
  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const onRunSettledRef = useRef(onRunSettled);
  onRunSettledRef.current = onRunSettled;

  const prevRunStatusRef = useRef<string | undefined>(undefined);
  const pendingRef = useRef<PendingRunReload | null>(null);

  const flush = useCallback((): void => {
    const pending = pendingRef.current;
    if (pending === null || chatStatusRef.current === 'streaming') {
      return;
    }
    pendingRef.current = null;
    // A session switch since the run finished means the result belongs to a
    // conversation the user has left; reloading would pull it into the wrong
    // one. Dropped deliberately — and recorded, so it is not another silent
    // exit on the very chain this hook exists to make observable.
    if (pending.sessionId !== sessionIdRef.current) {
      recordWorkflowDelivery({ ...pending.event, skipped: 'session-switched' });
      return;
    }
    void deliverRunResult(pending.event, reloadRef.current);
  }, []);

  // Schedule the refresh when the watched run reaches a terminal status.
  useEffect(() => {
    const wasRunning = prevRunStatusRef.current === 'running';
    prevRunStatusRef.current = runStatus;
    if (!wasRunning || runStatus === undefined || runStatus === 'running') {
      return;
    }
    const event: WorkflowDeliveryEvent = {
      runId: record?.runId ?? '',
      workflowId: record?.workflowId ?? '',
      status: runStatus,
    };
    const settledSessionId = sessionIdRef.current;
    const timer = setTimeout(() => {
      onRunSettledRef.current();
      if (chatStatusRef.current === 'streaming') {
        pendingRef.current = { sessionId: settledSessionId, event };
        return;
      }
      void deliverRunResult(event, reloadRef.current);
    }, RUN_RESULT_REFRESH_DELAY_MS);
    return () => clearTimeout(timer);
    // Keyed on the run-status transition only; `record` is read for its ids at
    // fire time and must not re-arm the timer when the poller rewrites it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runStatus]);

  // Flush a deferred refresh the moment the chat goes idle. This is the fix:
  // without it, `chatStatus === 'streaming'` at the 6s mark lost the result for
  // good, because nothing ever re-ran the effect above.
  useEffect(() => {
    if (chatStatus !== 'streaming') {
      flush();
    }
  }, [chatStatus, flush]);
}
